import { Button } from "@admin/components/atoms/Button";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { FoodPoisoningModal } from "@admin/pages/food-management/control-points/components/Modals/FoodPoisoningModal";
import {
  buttonColorMap,
  FoodPoisoning,
  getPoisiningById,
  StatusBgColors,
} from "@enos-monorepo/lib";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface PoisoningInfoModalProps extends ModalProps {
  selectedPoisonings: FoodPoisoning[];
  companyId: string;
  selectedLocationId: string;
}

export function PoisoningInfoModal({
  selectedPoisonings,
  companyId,
  selectedLocationId,
  ...props
}: PoisoningInfoModalProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPoisining, setSelectedPoisining] = useState<FoodPoisoning>();

  const { t } = useTranslation();
  const fetchAllData = async () => {
    if (selectedPoisonings.length === 0) {
      return;
    }

    const promises = selectedPoisonings.map((poisining) =>
      getPoisiningById(poisining.id),
    );

    return Promise.all(promises);
  };

  const { data } = useQuery({
    queryKey: ["foodControlDetails", selectedPoisonings],
    queryFn: fetchAllData,
    enabled: selectedPoisonings.length > 0,
  });

  const onEdit = (poisining: FoodPoisoning) => {
    setSelectedPoisining(poisining);
    setIsModalOpen(true);
  };

  return (
    <Modal {...props}>
      <div className="flex flex-col gap-5">
        {selectedPoisonings?.map((p) => {
          const bgColorClass = StatusBgColors.completedWithErrors;

          const ButtonColor = buttonColorMap.completedWithErrors;
          return (
            <div className={`${bgColorClass} rounded-xl`} key={p.id}>
              <div className="flex flex-row justify-end">
                <Button
                  size="sm"
                  property={ButtonColor}
                  onClick={() => onEdit(p)}
                >
                  Edit
                </Button>
              </div>
              <div className="px-5 py-8">
                <Label title={t("food.Date and Time")}>
                  <div>
                    {p?.creationDate &&
                      format(p?.creationDate, "dd.MM.yyyy - HH:mm")}
                  </div>
                </Label>
                <Label title={t("food.What food was consumed")}>
                  {p.poisoningDish}
                </Label>
                <Label title={t("food.Are there any food residues")}>
                  {p.leftOvers}
                </Label>
                <Label title={t("food.How many people consumed")}>
                  {p.numberConsumed}
                </Label>
                <Label title={t("food.Number of patients")}>
                  {p.sickAmount}
                </Label>
                <Label title={t("food.Date and Time of Poisoning")}>
                  {p?.poisoningDate &&
                    format(p?.poisoningDate, "dd.MM.yyyy - HH:mm")}
                </Label>
                <Label title={t("food.Date and Time of Consumption")}>
                  {p?.consumptionDate &&
                    format(p?.consumptionDate, "dd.MM.yyyy - HH:mm")}
                </Label>
                <Label title={t("food.Date and Time of Symptoms")}>
                  {p?.symptomsDate &&
                    format(p?.symptomsDate, "dd.MM.yyyy - HH:mm")}
                </Label>
                <Label title={t("food.Symptoms")}>
                  {p.symptoms ? p.symptoms : "-"}
                </Label>
                <Label title={t("food.Doctor Appointment")}>
                  {p.visitedDoctor ? p.visitedDoctor : "-"}
                </Label>
                <Label title={t("food.Contacted the food authority")}>
                  {p.contactedAuthority ? p.contactedAuthority : "-"}
                </Label>
                <Label title={t("food.Remark")}>{p.note ? p.note : "-"}</Label>
              </div>
            </div>
          );
        })}
      </div>
      {process.env.NODE_ENV === "development" && (
        <ul>
          Incoming Data
          {data?.map((detail, index) => (
            <li key={index}>
              <div
                style={{
                  border: "1px solid #ccc",
                  marginBottom: "10px",
                  padding: "10px",
                }}
              >
                {detail &&
                  Object.entries(detail)?.map(([key, value]) => (
                    <div key={key}>
                      <strong>{key}:</strong>{" "}
                      {typeof value === "object"
                        ? JSON.stringify(value)
                        : value?.toString()}
                    </div>
                  ))}
              </div>
            </li>
          ))}
        </ul>
      )}
      <FoodPoisoningModal
        title={t("food.Food Poisoning")}
        companyId={companyId}
        companyLocationId={selectedLocationId}
        initialData={selectedPoisining}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </Modal>
  );
}
